import { useState } from "react";

import { TextField } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { calcElectricEmissionsPerUnitValue } from "@aclymatepackages/calcs/utilities";

import {
  buildInitialTransactionInputValueFromSchema,
  isEmissionsInputSchemaEnabled,
} from "../../../helpers/components/inputs";
import { useResolvedUtilityEGrid } from "../../../helpers/components/utilityEGrid";
import { useAccountData } from "../../../helpers/firebase";
import { useOfficesWithCurrentChanges } from "../../../helpers/components/offices";

const ChillWaterInput = ({ chillWaterKwh, editChillWaterData, disabled }) => {
  return (
    <TextField
      label="Chill Water Kwh Used"
      value={chillWaterKwh}
      setValue={editChillWaterData("chillWaterKwh")}
      disabled={disabled}
    />
  );
};

const useChillWaterInput = ({ transaction, onSave }) => {
  const { office, status, date, emissionData } = transaction;

  const [offices] = useOfficesWithCurrentChanges();
  const [{ address }, accountDataLoading] = useAccountData();

  const inputSchema = [{ field: "chillWaterKwh" }];

  const initialTransactionInputValue =
    buildInitialTransactionInputValueFromSchema(transaction, inputSchema);

  const [chillWaterData, setChillWaterData] = useState(
    initialTransactionInputValue
  );

  const selectedOffice = offices.find(({ id }) => id === office?.id);
  const { currentUtilities, utilities } = selectedOffice || {};

  // The old lookup went through /egrid-tons-per-mwh, which only knows US
  // subregions (a stored "bc" silently became MROE), and with no office and no
  // stored company eGrid it priced chill water at 0 t/MWh.
  const { eGrid, eGridLoading } = useResolvedUtilityEGrid(
    [
      { storedEGrids: [emissionData?.eGrid] },
      {
        storedEGrids: [
          (currentUtilities || utilities)?.eGrid,
          selectedOffice?.address?.eGrid,
        ],
        address: selectedOffice?.address,
      },
      { storedEGrids: [address?.eGrid], address },
    ],
    { keepStoredUnresolved: false }
  );

  const { chillWaterKwh } = chillWaterData || {};

  const editChillWaterData = (field) => (value) =>
    editObjectData(setChillWaterData, field, value);

  const tonsCo2e = calcElectricEmissionsPerUnitValue({
    unitValue: Number(chillWaterKwh) || 0,
    unit: "kwh",
    eGrid,
    date: date || new Date(),
  });

  const saveEnabled =
    !eGridLoading &&
    !accountDataLoading &&
    isEmissionsInputSchemaEnabled(chillWaterData, inputSchema);

  const onTransactionSave = () =>
    onSave({
      tonsCo2e,
      emissionData: { ...chillWaterData, ...(eGrid ? { eGrid } : {}) },
    });

  return {
    inputBlock: (
      <ChillWaterInput
        editChillWaterData={editChillWaterData}
        {...chillWaterData}
        disabled={status === "locked"}
      />
    ),
    saveEnabled,
    onTransactionSave,
    tonsCo2e,
  };
};
export default useChillWaterInput;
